import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { ShieldCheck, Building2, AlertTriangle, ChevronDown, ChevronUp, Globe, Star } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#B27516', slate: '#5C6B7A' };
const fmtDate = d => { try { return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; } };
const yrs = d => { try { return Math.max(0, Math.floor((Date.now() - new Date(d)) / 3.156e10)); } catch { return null; } };

const ZONE_COLOR = z => /high|extreme|4|5/i.test(z || '') ? C.red : /med|moderate|2|3/i.test(z || '') ? C.amber : C.slate;

export default function EntityEnrichmentCard({ entity }) {
  const [open, setOpen] = useState(true);
  if (!entity || entity.virtual) return null;
  const age = yrs(entity.registration_date);
  const statusColor = entity.status === 'Active' ? C.green : C.red;
  const hazards = [
    ['Flood', entity.flood_zone], ['Fire', entity.fire_zone],
    ['Cyclone', entity.cyclone_zone], ['Earthquake', entity.earthquake_zone],
  ].filter(([, v]) => v);
  const flags = [
    entity.adverse_news_flag && 'Adverse news',
    entity.financial_distress_flag && 'Financial distress',
    entity.litigation_flag && 'Litigation',
    entity.insolvency_history && 'Insolvency history',
    entity.regulatory_action_flag && 'Regulatory action',
    entity.ato_debt_flag && 'ATO debt',
    entity.director_adverse_flag && 'Director adverse',
  ].filter(Boolean);

  return (
    <Card className="rounded-sm border bg-white" style={{ borderColor: 'rgba(26,76,124,0.2)' }} data-testid="entity-enrichment-card">
      <button type="button" onClick={() => setOpen(!open)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-[#F5F7FA] transition-colors">
        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider" style={{ color: C.blue }}>
          <ShieldCheck className="w-4 h-4" />Entity Intelligence
          <span className="px-2 py-0.5 rounded-full text-[9px] font-bold normal-case" data-testid="entity-status-badge"
            style={{ background: statusColor + '15', color: statusColor }}>{entity.status || 'Unknown'}</span>
        </span>
        <span className="flex items-center gap-2">
          {entity.data_completeness_pct != null && (
            <span className="text-[10px] font-mono" style={{ color: C.slate }}>{entity.data_completeness_pct}% complete</span>
          )}
          {open ? <ChevronUp className="w-4 h-4" style={{ color: C.slate }} /> : <ChevronDown className="w-4 h-4" style={{ color: C.slate }} />}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div data-testid="enrichment-registration">
            <GroupHead icon={Building2} title="Registration" />
            <Item label="Entity type" value={entity.entity_type} />
            <Item label="Registered" value={entity.registration_date ? `${fmtDate(entity.registration_date)}${age != null ? ` (${age} yrs)` : ''}` : '—'} />
            <Item label="GST registered" value={entity.gst_registered == null ? '—' : entity.gst_registered ? 'Yes' : 'No'}
              color={entity.gst_registered ? C.green : C.amber} />
            {entity.company_number && <Item label="Company no." value={entity.company_number} mono />}
          </div>
          <div data-testid="enrichment-profile">
            <GroupHead icon={Globe} title="Business Profile" />
            <Item label="Turnover band" value={entity.turnover_band} />
            <Item label="Employees" value={entity.employee_count_exact ?? entity.employee_count_band} />
            <Item label="Directors" value={entity.director_count} />
            {entity.google_rating != null && (
              <div className="flex items-center justify-between py-0.5 text-xs">
                <span style={{ color: C.slate }}>Google rating</span>
                <span className="flex items-center gap-1 font-medium" style={{ color: C.navy }}>
                  <Star className="w-3 h-3" style={{ color: '#E8A020', fill: '#E8A020' }} />{entity.google_rating} ({entity.google_review_count || 0})
                </span>
              </div>
            )}
            {entity.website && (
              <a href={entity.website} target="_blank" rel="noreferrer" className="text-xs hover:underline block truncate mt-0.5" style={{ color: C.blue }}>
                {entity.website.replace(/^https?:\/\//, '')}
              </a>
            )}
          </div>
          <div data-testid="enrichment-risk">
            <GroupHead icon={AlertTriangle} title="Risk Intelligence" />
            <Item label="Risk band" value={entity.risk_band} color={/high/i.test(entity.risk_band || '') ? C.red : /med/i.test(entity.risk_band || '') ? C.amber : C.green} />
            <Item label="Credit band" value={entity.credit_score_band} />
            {hazards.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {hazards.map(([k, v]) => (
                  <span key={k} className="px-1.5 py-0.5 rounded text-[9px] font-medium" style={{ background: ZONE_COLOR(v) + '12', color: ZONE_COLOR(v) }}>{k}: {v}</span>
                ))}
              </div>
            )}
            {flags.length > 0 ? (
              <div className="flex flex-wrap gap-1 mt-1.5" data-testid="enrichment-flags">
                {flags.map(f => (
                  <span key={f} className="px-1.5 py-0.5 rounded text-[9px] font-semibold" style={{ background: 'rgba(226,75,74,0.1)', color: C.red }}>⚑ {f}</span>
                ))}
              </div>
            ) : (
              <p className="text-[10px] mt-1.5" style={{ color: C.green }}>No adverse flags on record</p>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function GroupHead({ icon: Icon, title }) {
  return (
    <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: C.slate }}>
      <Icon className="w-3 h-3" />{title}
    </p>
  );
}

function Item({ label, value, mono, color }) {
  return (
    <div className="flex items-center justify-between py-0.5 text-xs">
      <span style={{ color: C.slate }}>{label}</span>
      <span className={mono ? 'font-mono' : 'font-medium'} style={{ color: color || C.navy }}>{value ?? '—'}</span>
    </div>
  );
}
